// Static, localized catalog. Structural data (ids, slugs, categories,
// coords, image counts, discounts, stamp relationships) lives in
// catalog.base.json. Translatable prose lives in catalog.{lang}.json,
// with English (catalog.en.json) as the canonical source and fallback.
// The frontend renders from these JSON files — the API is used only at
// runtime for auth + claiming stamps.
import base from './catalog.base.json';
import en from './catalog.en.json';
import hy from './catalog.hy.json';
import ru from './catalog.ru.json';
import fr from './catalog.fr.json';
import ka from './catalog.ka.json';
import it from './catalog.it.json';
import zh from './catalog.zh.json';

// Location body prose lives in Markdown, one file per venue per language:
//   ./locations/{slug}/{lang}.md
// The `?mdhtml` query routes each file through scripts/vite-plugin-md.mjs,
// which converts it at BUILD time to { html, metaText } — the full body as
// HTML plus the first paragraph as plain text for meta/search. Eager +
// build-time means no markdown parser ships to the browser and the HTML is
// baked into the prerendered pages.
type MdModule = {
  html: string;
  leadHtml: string;
  bodyHtml: string;
  metaText: string;
};
const MD_MODULES = import.meta.glob<MdModule>('./locations/*/*.md', {
  query: '?mdhtml',
  eager: true,
});

// Index MD modules by "slug/lang" for O(1) lookup. Path shape:
// ./locations/lavash-masterclass/en.md
const MD_BY_KEY: Record<string, MdModule> = {};
for (const [path, mod] of Object.entries(MD_MODULES)) {
  const m = path.match(/\/locations\/([^/]+)\/([^/]+)\.md$/);
  if (m) MD_BY_KEY[`${m[1]}/${m[2]}`] = mod;
}

// Resolve a venue's Markdown for a language, English-fallback if missing —
// mirrors the per-field prose fallback below.
function resolveMd(slug: string, lang: string): MdModule {
  return (
    MD_BY_KEY[`${slug}/${lang}`] ??
    MD_BY_KEY[`${slug}/en`] ?? {
      html: '',
      leadHtml: '',
      bodyHtml: '',
      metaText: '',
    }
  );
}

export type CatalogLocation = {
  id: string;
  slug: string;
  name: string;
  // Full venue body prose, rendered from Markdown to HTML at build time.
  // Render with dangerouslySetInnerHTML inside a prose-styled container.
  descriptionHtml: string;
  // Same body split at the first paragraph: `leadHtml` is the opening
  // paragraph, `bodyHtml` is everything after it. LocationDetails renders
  // the "You'll earn" stamp card between the two.
  leadHtml: string;
  bodyHtml: string;
  // First paragraph of the Markdown, plain text — used for the <meta
  // description>, og:description, and the client-side search haystack.
  metaText: string;
  category: string;
  region: string | null;
  address: string;
  latitude: number | null;
  longitude: number | null;
  imageCount: number;
  discountPercent: number | null;
  // Whether this venue offers a gift perk (rendered as a "Gift" tag
  // wherever the discount tag shows). Independent of discountPercent.
  hasGift: boolean;
  // Whether the venue is shown publicly. Hidden venues (visible=false) are
  // filtered out of every display surface — see VISIBLE_BASE below.
  visible: boolean;
  hours: string | null;
  findInstructions: string | null;
  stampId: string;
  stampName: string;
};

export type CatalogStamp = {
  id: string;
  name: string;
  locations: { id: string; slug: string; name: string; region: string | null }[];
};

// Each per-language file is a "prose pack" keyed by slug/id.
// Body prose (description/subDescription) moved to Markdown files; the
// remaining JSON prose is short structural-adjacent strings.
type LocProse = Partial<{
  name: string;
  region: string;
  address: string;
  hours: string;
  findInstructions: string;
}>;
type StampProse = Partial<{ name: string }>;
type LangPack = {
  locations: Record<string, LocProse>;
  stamps: Record<string, StampProse>;
};

const PACKS: Record<string, LangPack> = {
  en: en as unknown as LangPack,
  hy: hy as unknown as LangPack,
  ru: ru as unknown as LangPack,
  fr: fr as unknown as LangPack,
  ka: ka as unknown as LangPack,
  it: it as unknown as LangPack,
  zh: zh as unknown as LangPack,
};

const TEXT_FIELDS = [
  'name',
  'region',
  'address',
  'hours',
  'findInstructions',
] as const satisfies readonly (keyof LocProse)[];

// Structural row from catalog.base.json (no prose fields). Markdown-derived
// fields (descriptionHtml/metaText) and stampName are added in getLocations.
type LocationBase = Omit<
  CatalogLocation,
  | (typeof TEXT_FIELDS)[number]
  | 'stampName'
  | 'descriptionHtml'
  | 'leadHtml'
  | 'bodyHtml'
  | 'metaText'
> & { stampName?: string };

// Denormalized link to venues that grant this stamp. Names/regions
// come from the prose layer at render time.
type StampBase = {
  id: string;
  locations: { id: string; slug: string }[];
};

// Single filtered source of truth for what the public sees. Every display
// surface (lists, detail pages, prerender, sitemap, counts) derives from
// this — a venue with visible=false in catalog.base.json is absent here and
// therefore invisible everywhere. Flip the flag (DB + snapshot, or base.json)
// and rebuild to bring one back. New venues default to visible.
const VISIBLE_BASE = (base.locations as LocationBase[]).filter(
  (l) => l.visible,
);
const VISIBLE_SLUGS = new Set(VISIBLE_BASE.map((l) => l.slug));

function packFor(lang: string): LangPack {
  return PACKS[lang] ?? PACKS.en;
}

// Read prose for a location: try the target language, fall back to
// English on a per-field basis so partial translations never blank UI.
function resolveLocProse(slug: string, lang: string): Required<LocProse> {
  const target = packFor(lang).locations[slug] ?? {};
  const fallback = PACKS.en.locations[slug] ?? {};
  const out = {} as Record<string, unknown>;
  for (const f of TEXT_FIELDS) {
    out[f] = target[f] ?? fallback[f] ?? null;
  }
  return out as Required<LocProse>;
}

function resolveStampName(stampId: string, lang: string): string {
  return (
    packFor(lang).stamps[stampId]?.name ??
    PACKS.en.stamps[stampId]?.name ??
    ''
  );
}

export function getLocations(lang: string): CatalogLocation[] {
  return VISIBLE_BASE.map((b) => {
    const prose = resolveLocProse(b.slug, lang);
    const md = resolveMd(b.slug, lang);
    return {
      ...b,
      ...prose,
      descriptionHtml: md.html,
      leadHtml: md.leadHtml,
      bodyHtml: md.bodyHtml,
      metaText: md.metaText,
      stampName: resolveStampName(b.stampId, lang),
    } as CatalogLocation;
  });
}

export function getLocationBySlug(
  lang: string,
  slug: string,
): CatalogLocation | undefined {
  return getLocations(lang).find((l) => l.slug === slug);
}

export function getStamps(lang: string): CatalogStamp[] {
  return (base.stamps as StampBase[])
    .map((b) => ({
      id: b.id,
      name: resolveStampName(b.id, lang),
      // Only venues that are publicly visible; a stamp granted solely by
      // hidden venues is dropped below.
      locations: b.locations
        .filter((loc) => VISIBLE_SLUGS.has(loc.slug))
        .map((loc) => {
          const prose = resolveLocProse(loc.slug, lang);
          return {
            id: loc.id,
            slug: loc.slug,
            name: prose.name ?? '',
            region: prose.region ?? null,
          };
        }),
    }))
    .filter((s) => s.locations.length > 0);
}

// Resolve a single stamp by id WITHOUT the visibility filter — used to name
// stamps a user has already earned, so their passport still renders those
// stamps even when the granting venue is now hidden. Returns undefined only
// for an id absent from the catalog entirely.
export function getStampById(
  lang: string,
  stampId: string,
): CatalogStamp | undefined {
  const b = (base.stamps as StampBase[]).find((s) => s.id === stampId);
  if (!b) return undefined;
  return {
    id: b.id,
    name: resolveStampName(b.id, lang),
    locations: b.locations.map((loc) => {
      const prose = resolveLocProse(loc.slug, lang);
      return {
        id: loc.id,
        slug: loc.slug,
        name: prose.name ?? '',
        region: prose.region ?? null,
      };
    }),
  };
}

// Slugs of publicly visible venues only. Used for prerender + sitemap, so
// hidden venues get no static HTML and no sitemap entry.
export const ALL_SLUGS = VISIBLE_BASE.map((l) => l.slug);

// Total number of venues, derived from the structural catalog so any
// count shown to users (e.g. the home-page "Verified Venues" stat) stays
// in sync automatically when a venue is added or removed. Never hardcode
// this number — import LOCATION_COUNT instead.
export const LOCATION_COUNT = ALL_SLUGS.length;

// Category labels are structural (identical in every language in
// catalog.base.json), so they're localized via i18n keys rather than the
// prose layer. Map the raw category string to its locale key; callers do
// `t(categoryKey(location.category))`. Unknown categories fall back to the
// raw string so nothing ever renders blank.
const CATEGORY_KEYS: Record<string, string> = {
  Accommodation: 'category.accommodation',
  Adventure: 'category.adventure',
  'Food & Drink': 'category.food_drink',
  Masterclasses: 'category.masterclasses',
  Sightseeing: 'category.sightseeing',
};

export function categoryKey(category: string): string {
  return CATEGORY_KEYS[category] ?? category;
}
